import { Link } from '@tanstack/react-router'
import { Mail, MapPin, Phone } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { ProjectCustomerThreads } from '@/features/projects/project-customer-threads'
import { TrackerPortalButton } from '@/features/projects/tracker-portal-button'
import { ProjectQuoteSummary } from '@/features/quote-builder/project/project-quote-link'
import type { components } from '@/api/schema'

type CustomerThread = components['schemas']['ProjectCustomerCommunicationThreadRead']

export interface CustomerShipTo {
  ship_to_name?: string | null
  ship_to_company?: string | null
  ship_to_address1?: string | null
  ship_to_address2?: string | null
  ship_to_city?: string | null
  ship_to_state?: string | null
  ship_to_zip?: string | null
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/** The ship-to name and company, each once, leaving out what repeats the customer's name above. */
function shipToNames(shipTo: CustomerShipTo, customerName: string) {
  const lines: string[] = []
  for (const value of [shipTo.ship_to_name, shipTo.ship_to_company]) {
    const line = value?.trim()
    if (line && !sameName(line, customerName) && !lines.some((seen) => sameName(seen, line))) {
      lines.push(line)
    }
  }
  return lines
}

/**
 * Who the project is for: the name across the top, then contact on the left and ship-to on the
 * right (stacked when narrow), then the customer's conversations and the quote the project started from.
 */
export function ProjectCustomerCard({
  projectId,
  name,
  customerGroupUuid,
  email,
  phone,
  shipTo,
  threads,
  threadsLoading,
}: {
  projectId: string
  name: string | null | undefined
  customerGroupUuid?: string | null
  email?: string | null
  phone?: string | null
  shipTo?: CustomerShipTo
  threads: CustomerThread[] | undefined
  threadsLoading: boolean
}) {
  return (
    <Card className="gap-0 py-0">
      <CardContent className="@container/customer space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Customer</h3>
          <TrackerPortalButton projectId={projectId} />
        </div>
        {name ? (
          <div className="space-y-2">
            {/* The name gets the full width: in half a sidebar a long one wraps word by word. */}
            <p className="font-semibold">
              {customerGroupUuid ? (
                <Link
                  to="/customers/$groupId"
                  params={{ groupId: customerGroupUuid }}
                  className="text-primary hover:underline"
                >
                  {name}
                </Link>
              ) : (
                name
              )}
            </p>
            <div
              className="grid grid-cols-1 gap-x-4 gap-y-2 @[17rem]/customer:grid-cols-2"
              data-testid="project-customer-details"
            >
              {email || phone ? (
                <div className="min-w-0 space-y-1.5">
                  {email && (
                    <div className="flex min-w-0 items-center gap-2 text-sm">
                      <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      {/* eslint-disable-next-line no-restricted-syntax -- mailto link */}
                      <a
                        href={`mailto:${email}`}
                        title={email}
                        className="min-w-0 truncate text-primary hover:underline"
                      >
                        {email}
                      </a>
                    </div>
                  )}
                  {phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      {/* eslint-disable-next-line no-restricted-syntax -- tel link */}
                      <a href={`tel:${phone}`} className="whitespace-nowrap text-primary hover:underline">
                        {phone}
                      </a>
                    </div>
                  )}
                </div>
              ) : null}
              {shipTo?.ship_to_address1 && (
                <address
                  aria-label="Ship to"
                  className="flex min-w-0 items-start gap-2 text-sm not-italic"
                >
                  <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  <div className="min-w-0 text-muted-foreground">
                    {shipToNames(shipTo, name).map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                    <p>{shipTo.ship_to_address1}</p>
                    {shipTo.ship_to_address2 && <p>{shipTo.ship_to_address2}</p>}
                    <p>
                      {shipTo.ship_to_city}{shipTo.ship_to_state ? `, ${shipTo.ship_to_state}` : ''} {shipTo.ship_to_zip}
                    </p>
                  </div>
                </address>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">No customer info</p>
        )}
        <ProjectCustomerThreads threads={threads} isLoading={threadsLoading} />
        <ProjectQuoteSummary projectUuid={projectId} />
      </CardContent>
    </Card>
  )
}
